<template>
  <div class="m-0" v-if="isManeuverRestitutionRequest">
    <div class="text-xs-center" v-if="displayComputationWidget">
      <exo-mission-computation></exo-mission-computation>
    </div>
    <div v-if="displayResult">
      <v-row>
        <v-col class="pb-3">
          <v-row no-gutters>
            <div class="d-inline-flex ml-3 justify-center align-center">
              <span class="headline grey--text">{{
                currentMission.name.toUpperCase()
              }}</span>
            </div>
            <v-spacer></v-spacer>
            <v-bottom-sheet inset v-if="displayWarning">
              <template v-slot:activator="{ on: bottomSheet }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                      large
                      icon
                      color="warning"
                      v-on="{ ...tooltip, ...bottomSheet }"
                    >
                      <icon
                        scale="1.5"
                        color="warning"
                        class="orange--text"
                        name="exclamation"
                      ></icon>
                    </v-btn>
                  </template>
                  <span>Alert message</span>
                </v-tooltip>
              </template>
              <v-card>
                <div class="alert-div"></div>
                <v-alert
                  outlined
                  :value="true"
                  type="warning"
                  class="m-0"
                  v-if="displayWarningEccentricity"
                >
                  Disclaimer: eccentricity might change during the manoeuvres
                  with system constraints. Please let us know if the final orbit
                  does not match your expectation during a mission. We will
                  investigate and fix it.
                </v-alert>
                <v-alert
                  outlined
                  :value="true"
                  type="warning"
                  class="m-0"
                  v-if="displayWarningDutyCycle"
                >
                  Disclaimer: the spacecraft orbital average power was not
                  enough to supply the propulsion system warm-up and thrust. To
                  respect the max mean duty cycle, the orbital duty cycle used
                  in the simulation was lower than the one selected by the user:
                  <br />
                  <i
                    >spacecraft definition duty cycle:
                    {{
                      (
                        100 *
                        this.currentResultsLog.results.maneuveringStrategy
                          .spacecraftDefinitionDutyCycle
                      ).toFixed(2)
                    }}
                    % / orbital duty cycle used in the simulation:
                    {{
                      (
                        100 *
                        this.currentResultsLog.results.maneuveringStrategy
                          .orbitalDutyCycle
                      ).toFixed(2)
                    }}
                    %</i
                  >
                </v-alert>
              </v-card>
            </v-bottom-sheet>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="saveLayout()"
                >
                  <icon scale="1.5" class="primary--text" name="save"></icon>
                </v-btn>
              </template>
              <span
                >Save the layout and the configurations of the dashboard</span
              >
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="resetLayout()"
                >
                  <icon
                    scale="1.5"
                    class="primary--text"
                    name="undo-alt"
                  ></icon>
                </v-btn>
              </template>
              <span
                >Reset the layout and the configurations of the dashboard</span
              >
            </v-tooltip>
          </v-row>
        </v-col>
      </v-row>
      <v-divider class="mt-0"></v-divider>
      <div class="px-3">
        <numerical-plot-config ref="plotConfig"></numerical-plot-config>
        <grid-layout
          :layout.sync="gridlayout"
          :col-num="6"
          :row-height="7"
          :is-draggable="true"
          :is-resizable="true"
          :is-mirrored="false"
          :vertical-compact="true"
          :margin="[20, 10]"
          :use-css-transforms="true"
        >
          <grid-item
            :x="gridlayout[0].x"
            :y="gridlayout[0].y"
            :w="gridlayout[0].w"
            :h="gridlayout[0].h"
            :i="gridlayout[0].i"
            :minH="5"
            :minW="1"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <stats-card
              v-if="this.currentResultsLog.results.measuresAfterTheConverter"
              color="#2A94DE"
              awesomeicon="tachometer-alt"
              title="Total ΔV"
              :ref="`${statsCardName}${gridlayout[0].i}`"
              :cardId="`${statsCardName}${gridlayout[0].i}`"
              :value="
                computeResultToDisplay([
                  'maneuverRestitutionReport',
                  'totalDeltaV'
                ])
              "
              :unit="
                getResultUnitLabel(['maneuverRestitutionReport', 'totalDeltaV'])
              "
            />
          </grid-item>
          <grid-item
            :x="gridlayout[1].x"
            :y="gridlayout[1].y"
            :w="gridlayout[1].w"
            :h="gridlayout[1].h"
            :i="gridlayout[1].i"
            :minH="5"
            :minW="1"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <stats-card
              color="#FF7E0D"
              icon="ev_station"
              title="Used
            propellant"
              :ref="`${statsCardName}${gridlayout[1].i}`"
              :cardId="`${statsCardName}${gridlayout[1].i}`"
              :value="
                computeResultToDisplay([
                  'maneuverRestitutionReport',
                  'totalConsumedPropellant'
                ])
              "
              :unit="
                getResultUnitLabel([
                  'maneuverRestitutionReport',
                  'totalConsumedPropellant'
                ])
              "
            />
          </grid-item>
          <grid-item
            :x="gridlayout[2].x"
            :y="gridlayout[2].y"
            :w="gridlayout[2].w"
            :h="gridlayout[2].h"
            :i="gridlayout[2].i"
            :minH="5"
            :minW="1"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <stats-card
              color="#F72C2C"
              icon="timer"
              title="Initial measurement date"
              :ref="`${statsCardName}${gridlayout[2].i}`"
              :cardId="`${statsCardName}${gridlayout[2].i}`"
              :value="
                computeResultToDisplay(['initialMeasurementDate'])
                  .toString()
                  .slice(0, 23)
              "
              :unit="getResultUnitLabel(['initialMeasurementDate'])"
            />
          </grid-item>
          <grid-item
            :x="gridlayout[3].x"
            :y="gridlayout[3].y"
            :w="gridlayout[3].w"
            :h="gridlayout[3].h"
            :i="gridlayout[3].i"
            :minH="5"
            :minW="1"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <stats-card
              color="#1d2939"
              icon="file_download_done"
              title="Nb of measures"
              :ref="`${statsCardName}${gridlayout[3].i}`"
              :cardId="`${statsCardName}${gridlayout[3].i}`"
              :value="
                computeResultToDisplay([
                  'orbitDeterminationReport',
                  'numberOfUsedMeasurements'
                ])
              "
              :unit="
                getResultUnitLabel([
                  'orbitDeterminationReport',
                  'numberOfUsedMeasurements'
                ])
              "
            />
          </grid-item>
          <!--grid-item
            :x="gridlayout[4].x"
            :y="gridlayout[4].y"
            :w="gridlayout[4].w"
            :h="gridlayout[4].h"
            :i="gridlayout[4].i"
            :minH="5"
            :minW="1"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <stats-card
              color="#B264C5"
              icon="file_download_done"
              title="Nb of measures"
              :ref="`${statsCardName}${gridlayout[4].i}`"
              :cardId="`${statsCardName}${gridlayout[4].i}`"
              :value="
                computeResultToDisplay([
                  'orbitDeterminationReport',
                  'numberOfUsedMeasurements'
                ])
              "
              :unit="
                getResultUnitLabel([
                  'orbitDeterminationReport',
                  'numberOfUsedMeasurements'
                ])
              "
            />
          </grid-item-->
          <grid-item
            v-if="
              this.currentResultsLog &&
              this.currentResultsLog.results &&
              this.currentResultsLog.results.maneuverRestitutionReport
            "
            :x="gridlayout[5].x"
            :y="gridlayout[5].y"
            :w="gridlayout[5].w"
            :h="gridlayout[5].h"
            :i="gridlayout[5].i"
            :isResizable="false"
          >
            <v-card-title class="pa-0">
              <span class="font-weight-light ml-2 text-truncate" primaryTitle
                >Firings
              </span>
            </v-card-title>
            <v-card tile height="100%">
              <v-data-table
                dense
                hide-default-footer
                :headers="firingsHeaders"
                :items="firings"
                height="370"
              >
              </v-data-table>
            </v-card>
          </grid-item>

          <div v-for="config in configurations" :key="config.id">
            <grid-item
              v-if="config.gridItem"
              :x="config.gridItem.x"
              :y="config.gridItem.y"
              :w="config.gridItem.w"
              :h="config.gridItem.h"
              :i="config.gridItem.i"
              :isDraggable="config.isDraggable"
              @resize="plotCardResized"
              @resized="plotCardResized"
            >
              <v-card tile height="100%">
                <v-row>
                  <v-col cols="12">
                    <v-btn
                      color="primary"
                      @click.stop="editPlotConfiguration(0)"
                      left
                      dark
                      text
                    >
                      <v-icon dark medium>edit</v-icon>
                      Edit
                    </v-btn>
                  </v-col>
                </v-row>
                <plot-item
                  :gridlayout="gridlayout"
                  height="100%"
                  :root="_self"
                  :config="config"
                  :id="`${plotingAreaName}${config.gridItem.i}`"
                  :ref="`${plotingAreaName}${config.gridItem.i}`"
                  class="elevation-5"
                >
                </plot-item>
              </v-card>
            </grid-item>
          </div>
        </grid-layout>
      </div>
    </div>
  </div>
</template>

<script>
import dashboardComponents from '../../components/Dashboard/index.js'
import dataTools from '../../tools/data.js'
import { mapGetters } from 'vuex'
import Vue from 'vue'
import errorMixin from '../../mixins/logHandler.js'
import plotInput from '../../mixins/plotInput.js'
import plotLayout from '../../mixins/plotLayout.js'
import plotData from '../../mixins/plotData.js'
import Plotly from 'plotly.js-dist'
import { setTimeout } from 'timers'

Vue.use(dashboardComponents)

export default {
  name: 'ManeuverRestitutionDashboard',
  mixins: [errorMixin, plotLayout, plotInput, plotData],

  data() {
    return {
      configurations: [],
      isDrawn: [],
      drawer: false,
      plotingAreaName: 'plotingAreaRestitution',
      statsCardName: 'statsCard',
      type: 'ManeuverRestitutionResult',
      configuration: this.buildEmptyConfiguration(),
      defaultLayout: [
        { x: 0, y: 0, w: 1, h: 6, i: '0' },
        { x: 1, y: 0, w: 1, h: 6, i: '1' },
        { x: 2, y: 0, w: 1, h: 6, i: '2' },
        { x: 3, y: 0, w: 1, h: 6, i: '3' },
        { x: 4, y: 0, w: 1, h: 6, i: '4' },
        { x: 4, y: 0, w: 2, h: 22, i: '5' }
      ],
      gridlayout: [
        { x: 0, y: 0, w: 1, h: 6, i: '0' },
        { x: 1, y: 0, w: 1, h: 6, i: '1' },
        { x: 2, y: 0, w: 1, h: 6, i: '2' },
        { x: 3, y: 0, w: 1, h: 6, i: '3' },
        { x: 4, y: 0, w: 1, h: 6, i: '4' },
        { x: 4, y: 0, w: 2, h: 22, i: '5' }
      ],
      firingsHeaders: [
        {
          text: 'Index',
          align: 'start',
          sortable: false,
          value: 'index'
        },
        { text: 'Start date', value: 'estimatedStartManeuverDate' },
        { text: 'End date', value: 'estimatedEndManeuverDate' },
        {
          text: 'ΔV' + ' (' + dataTools.getDefaultGuiUnit('velocity') + ')',
          value: 'estimatedManeuverDeltaV'
        },
        {
          text: 'Direction (x, y, z)',
          value: 'estimatedManeuverDirection'
        },
        {
          text: 'Thrust' + ' (' + dataTools.getDefaultGuiUnit('force') + ')',
          value: 'estimatedManeuverThrust'
        },
        {
          text:
            'Consumed propellant' +
            ' (' +
            dataTools.getDefaultGuiUnit('consumption') +
            ')',
          value: 'estimatedManeuverConsumedPropellant'
        }
      ]
    }
  },

  created() {
    window.addEventListener('beforeunload', this.saveLayout)
  },

  mounted() {
    this.$root.$on('current-results-updated', () => {
      this.loadLayout()
      this.removeDuplicatedGridItem()
    })
    this.$root.$on('create-plot', (config, plotingArea, fromLayoutSave) => {
      //Osculating SMA
      config.data[0].x =
        this.currentResultsLog.results.orbitDeterminationReport.estimatedOscStatesDates

      this.currentResultsLog.results.orbitDeterminationReport.estimatedStates.forEach(
        (el) => {
          config.data[0].y.push(dataTools.m2km(el[0]))
        }
      )

      //Mean SMA
      config.data[1].x =
        this.currentResultsLog.results.orbitDeterminationReport.estimatedOscStatesDates
      this.currentResultsLog.results.orbitDeterminationReport.estimatedMeanStates.forEach(
        (el) => {
          config.data[1].y.push(dataTools.m2km(el[0]))
        }
      )

      //Mean eccentricity
      config.data[2].x =
        this.currentResultsLog.results.orbitDeterminationReport.estimatedOscStatesDates
      this.currentResultsLog.results.orbitDeterminationReport.estimatedMeanStates.forEach(
        (el) => {
          config.data[2].y.push(Object.values(el)[1])
        }
      )
      //Mean inclination
      config.data[3].x =
        this.currentResultsLog.results.orbitDeterminationReport.estimatedOscStatesDates
      this.currentResultsLog.results.orbitDeterminationReport.estimatedMeanStates.forEach(
        (el) => {
          config.data[3].y.push(dataTools.rad2deg(el[2]))
        }
      )

      if (!fromLayoutSave && config.layout) {
        config.gridItem.h = 25
      }
      // Use timeout as workaround because nextTick doesnt work as expected
      setTimeout(() => {
        let el = this.$refs[`${this.plotingAreaName}${config.gridItem.i}`]
        if (el) {
          el = el[0]
          let parent = el ? el.$parent : undefined
          if (parent) {
            config.layout.height = parent.$el.clientHeight - 50
            if (
              config.data[0].x
                .filter((data) => !isNaN(data) || typeof data === 'string')
                .some(isNaN)
            ) {
              config.layout.xaxis.categoryorder = 'array'
              config.layout.xaxis.categoryarray = [...config.data[0].x] // to create a new array
              config.layout.xaxis.categoryarray.sort()
            }
            if (
              config.data[0].y
                .filter((data) => !isNaN(data) || typeof data === 'string')
                .some(isNaN)
            ) {
              config.layout.yaxis.categoryorder = 'array'
              config.layout.yaxis.categoryarray = [...config.data[0].y] // to create a new array
              config.layout.yaxis.categoryarray.sort()
            }
            Plotly.react(plotingArea, config.data, config.layout, {
              responsive: true
            })
            plotingArea.addEventListener('mouseover', (data) => {
              config.isDraggable = false
            })
            plotingArea.addEventListener('mouseleave', (data) => {
              config.isDraggable = true
            })
          }
        }
      }, 100)
    })
  },

  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      currentMission: 'currentMission',
      currentResultsLog: 'currentResultsLog'
    }),
    displayComputationWidget() {
      return this.computationNeeded || !this.resultsLoaded
    },
    displayResult() {
      return this.resultsLoaded && !this.computationNeeded
    },
    computationNeeded() {
      return (
        this.currentMission &&
        this.currentMission.computationRequested &&
        this.currentMission.computationLogId === ''
      )
    },
    resultsLoaded() {
      return (
        this.currentResultsLog != null && this.currentResultsLog.results != null
      )
    },
    resultAvailable() {
      return (
        this.resultsLoaded &&
        this.currentResultsLog.results ===
          Object(this.currentResultsLog.results)
      ) // check object, not array
    },
    ephemeridesAvailable() {
      return (
        this.resultAvailable &&
        this.currentResultsLog.results.ephemerides != null
      )
    },
    ephemeridesFilesAvailable() {
      return (
        this.resultAvailable &&
        this.currentResultsLog.results.ephemeridesFiles != null
      )
    },
    displayWarningEccentricity() {
      return this.currentMission.attitudeAndSystemSimulated
    },
    displayWarningDutyCycle() {
      let maneuveringStrategyResults =
        this.currentResultsLog.results.maneuveringStrategy
      if (maneuveringStrategyResults == null) {
        return false
      } else {
        return (
          maneuveringStrategyResults.spacecraftDefinitionDutyCycle >
          maneuveringStrategyResults.orbitalDutyCycle
        )
      }
    },
    displayWarning() {
      return this.displayWarningEccentricity || this.displayWarningDutyCycle
    },
    isEphemeridesFilesGenerated() {
      if (this.ephemeridesFilesAvailable) {
        return (
          Object.keys(this.currentResultsLog.results.ephemeridesFiles).length >
          0
        )
      }
      return false
    },
    firings() {
      let firings = []
      let results = this.currentResultsLog.results.maneuverRestitutionReport
      results.estimatedStartManeuverDates.forEach((value, index) => {
        const firing = {
          index: index,
          estimatedStartManeuverDate: value,

          estimatedEndManeuverDate: results.estimatedEndManeuverDates[index],

          estimatedManeuverDeltaV: results.estimatedManeuverDeltaVs[
            index
          ].toFixed(dataTools.getNbToDisplay('m/s')),

          estimatedManeuverDirection:
            results.estimatedManeuverDirections[index],

          estimatedManeuverThrust: dataTools
            .N2mN(results.estimatedManeuverThrusts[index])
            .toFixed(dataTools.getNbToDisplay('mN')),

          estimatedManeuverConsumedPropellant: dataTools
            .kg2g(results.estimatedManeuverConsumedPropellants[index])
            .toFixed(dataTools.getNbToDisplay('g'))
        }
        firings.push(firing)
      })
      return firings
    }
  },

  methods: {
    editPlotConfiguration(index) {
      this.configuration = this.configurations[index]
      this.$refs.plotConfig.drawer = true
      let plotingArea = document.getElementById(
        `${this.plotingAreaName}${this.configuration.gridItem.i}`
      )
      if (plotingArea != null) {
        this.$root.$emit('set-plot-config', this.configuration, plotingArea)
      }
    },

    removePlotConfiguration(gridIndex) {
      let index = this.configurations.findIndex((el) => {
        return el.gridItem.i === gridIndex
      })
      let currentConfig = this.configurations[index]
      let plotingArea = document.getElementById(
        `${this.plotingAreaName}${currentConfig.gridItem.i}`
      )
      if (plotingArea != null) {
        try {
          Plotly.purge(plotingArea)
        } catch (e) {}
      }
      this.configuration = undefined
      this.isDrawn.splice(index, 1)
      this.configurations.splice(index, 1)
      this.gridlayout = this.gridlayout.filter((el) => el.i !== gridIndex)
    },
    async extractMeasurements() {
      let measures = this.currentResultsLog.results.measures
      //TODO : pass computationLogId as argument to set measurement name in backend
      let data = await this.$http
        .post('/users/' + this.currentUser.id + '/' + 'measurements/extract', {
          measures
        })
        .then(function (response) {
          console.log('Data successfully extracted!')
          return response.data
        })
        .catch((e, __) => {
          this.errorTrace = this.getErrorMessage(e)
          return null
        })
      return data
    },

    resetLayout() {
      let mission = dataTools.saveGridLayout(
        this.currentMission,
        [],
        this.defaultLayout
      )
      this.$http
        .put(this.getCurrentAPI() + '/' + mission.id + '/minorupdate', mission)
        .then((response) => {
          this.$root.$emit('set-current-mission', response.data)
        })
    },
    saveLayout() {
      let mission = dataTools.saveGridLayout(
        this.currentMission,
        this.configurations,
        this.gridlayout
      )
      this.$http
        .put(this.getCurrentAPI() + '/' + mission.id + '/minorupdate', mission)
        .then((response) => {
          this.$root.$emit('set-current-mission', response.data)
        })
    },

    addDefaultAxis(axisList, condition, name) {
      if (condition) {
        let parameter = this.buidDefaultPlotParameter()
        parameter.value = name
        this.treeUpdated(parameter, 'parameter')
        axisList.push(parameter)
      }
    },

    loadLayout() {
      if (this.currentMission && this.gridlayout) {
        if (this.currentMission.layout) {
          let missionLayout = JSON.parse(this.currentMission.layout)
          dataTools.loadGridLayoutConfig(missionLayout, this)
        } else {
          let missionLayout = { configurations: [], layout: this.defaultLayout }
          dataTools.loadGridLayoutConfig(missionLayout, this)
          let defaultExists = false
          if (!defaultExists) {
            let config
            this.configurations = []
            config = this.buildEmptyConfiguration(0)
            config.name = this.currentMission.spaceObject.noradID
              ? 'Restitution for space object ' +
                this.currentMission.spaceObject.name +
                ' [' +
                this.currentMission.spaceObject.noradID +
                ']'
              : 'Restitution for space object ' +
                this.currentMission.spaceObject.name +
                ' [unknown ID]'
            config.type = 'Multi axis'
            config.multiAxis = []
            if (this.isManeuverRestitutionRequest(this.currentMission)) {
              this.addDefaultAxis(
                config.multiAxis,
                this.isManeuverRestitutionRequest(this.currentMission),
                'Osculating semi-major axis (km)'
              )
              this.addDefaultAxis(
                config.multiAxis,
                this.isManeuverRestitutionRequest(this.currentMission),
                'Mean semi-major axis (km)'
              )
              this.addDefaultAxis(
                config.multiAxis,
                this.isManeuverRestitutionRequest(this.currentMission),
                'Mean eccentricity '
              )
              this.addDefaultAxis(
                config.multiAxis,
                this.isManeuverRestitutionRequest(this.currentMission),
                'Mean inclination (deg)'
              )
            }
            let gridItem = {
              x: 0,
              y: 1,
              w: 4,
              h: 25,
              i: `plot0`
            }
            config.id = 0
            config.gridItem = gridItem
            this.configuration = config
            this.configurations.push(config)
            this.gridlayout.push(this.configuration.gridItem)
            this.$nextTick(() => {
              let plotingArea = document.getElementById(
                `${this.plotingAreaName}${this.configuration.gridItem.i}`
              )
              this.$root.$emit(
                'set-plot-config',
                this.configuration,
                plotingArea
              )
              this.basicPlotLoadWorkaround()
            })
          }
        }
      }
    },
    removeDuplicatedGridItem() {
      // remove duplicated items (same id
      for (let i = 0; i < this.configurations.length - 1; i++) {
        // remove configurations
        if (
          this.configurations[i].gridItem.i ===
          this.configurations[i + 1].gridItem.i
        ) {
          // same id
          this.configurations.splice(i + 1, 1) // remove the others
        }
      }
      if (this.gridlayout) {
        for (let i = 0; i < this.gridlayout.length - 1; i++) {
          // first remove duplicated ids
          if (this.gridlayout[i].i === this.gridlayout[i + 1].i) {
            // same id
            this.gridlayout.splice(i + 1, 1) // remove the others
          }
        }
        // remove ghosts : in the layout but no configuration
        for (let i = 0; i < this.gridlayout.length; i++) {
          let itemId = this.gridlayout[i].i
          if (itemId.startsWith('plot')) {
            let configuration = this.configurations.find(
              (el) => el.gridItem.i === itemId
            )
            if (configuration == null) {
              this.gridlayout.splice(i, 1)
              i = i - 1 // because i should not be increased
            }
          }
        }
      }
    },

    statsCardResized(i, newH, newW, newHPx, newWPx) {
      let card = this.$refs[`${this.statsCardName}${i}`]
      if (card != null) {
        card.updateCardWidth()
      }
    },

    // basicPlotLoadWorkaround is a function to handle the fact that plots may spawn at the bottom of the page
    // Unfortunately, we can't resolve it the proper way until we make some cleaning in the event bus ¯\_( ͡° ͜ʖ ͡°)_/¯
    basicPlotLoadWorkaround() {
      let grid = this.gridlayout
      let newGrid = []
      grid.forEach((el) => {
        let exists = newGrid.find((elB) => {
          return elB.i === el.i
        })
        if (!exists) {
          newGrid.push(el)
        }
      })
      this.gridlayout = newGrid
      let plot0Conf = this.configurations.find(
        (el) => el.gridItem.i === 'plot0'
      )
      if (plot0Conf != null) {
        plot0Conf.gridItem = this.gridlayout.find((el) => el.i === 'plot0')
      }
    },

    plotCardResized(i, newH, newW, newHPx, newWPx) {
      let update = {
        width: newWPx,
        height: newHPx - 50
      }
      let plotingArea = document.getElementById(`${this.plotingAreaName}${i}`)
      if (plotingArea != null) {
        try {
          Plotly.relayout(plotingArea, update)
        } catch (e) {
          console.log('Error calling Plotly.relayout')
        }
      }
    },
    invertPlotDrawn(index) {
      this.isDrawn[index] = !this.isDrawn[index]
    },

    addPlotConfiguration() {
      this.removeDuplicatedGridItem()

      let lastId =
        this.configurations.length > 0
          ? this.configurations.reduce((acc, curr) => {
              return curr.id > acc.id ? curr : acc
            }).id
          : 0
      let newId = lastId + 1
      let newConfig = this.buildEmptyConfiguration(newId)
      this.configuration = newConfig
      let biggestY = this.gridlayout.reduce((acc, curr) => {
        return acc.y > curr.y ? acc : curr
      }, this.gridlayout[0])
      let gridItem = {
        x: 0,
        y: biggestY.y + biggestY.h,
        w: 4,
        h: 25,
        i: `${'plot' + newId}`
      }
      this.configuration.gridItem = gridItem
      this.configurations.push(this.configuration)
      this.$nextTick(() => {
        this.gridlayout.push(this.configuration.gridItem)
        this.$nextTick(() => {
          let plotingArea = document.getElementById(
            `${this.plotingAreaName}${this.configuration.gridItem.i}`
          )
          if (plotingArea != null) {
            this.$root.$emit('set-plot-config', this.configuration, plotingArea)
          }
          this.$refs.plotConfig.drawer = !this.$refs.plotConfig.drawer
        })
      })
    },

    getResultUnitLabel(fieldPath) {
      let fieldType = dataTools.getFieldTypeFromPath(fieldPath, this.type, null)
      return (
        ' ' + dataTools.getUnitDisplayed(dataTools.getDefaultGuiUnit(fieldType))
      )
    },

    buildPath(path) {
      if (!this.resultAvailable) {
        return
      }
      let field = this.currentResultsLog.results
      for (let i = 0; i < path.length; i++) {
        field = field[path[i]]
      }
      return field
    },

    computeResultToDisplay(fieldPath) {
      let fieldType = dataTools.getFieldTypeFromPath(fieldPath, this.type, null)
      if (fieldPath.includes('ephemerides')) {
        // range shown
        return '???'
      } else {
        return dataTools
          .convertSi2Display(fieldType, this.buildPath(fieldPath))
          .toString()
      }
    },

    computeRange(fieldType, fieldPath) {
      if (!this.resultAvailable) {
        return ''
      }
      let data = dataTools.convertSimulationData(
        this.currentResultsLog.results.ephemerides,
        this.currentResultsLog.results.fieldIndexes
      )
      let allVals = dataTools
        .getUniqueValues(data, fieldPath)
        .filter((value) => !isNaN(value))
      let min = dataTools.convertSi2Display(fieldType, Math.min(...allVals))
      let max = dataTools.convertSi2Display(fieldType, Math.max(...allVals))
      if (min === max) {
        return min.toString()
      } else {
        if (min.toString().includes('Infinity')) {
          return ''
        }
        let displayedString = '[' + min
        allVals.length === 2
          ? (displayedString += '; ')
          : (displayedString += ' ... ')
        return displayedString + max + ']'
      }
    },

    getMissionItem(mission) {
      if (mission && mission.constructor === Array) {
        return mission[0]
      } else {
        return mission
      }
    },

    // TODO MFI most of these methos should be deleted
    isMissionType(mission, type) {
      return (
        this.getMissionItem(mission) &&
        this.getMissionItem(mission).type === type
      )
    },

    isSimulatorMission(mission) {
      return this.isMissionType(mission, 'Simulator')
    },

    isManeuverRestitutionRequest(mission) {
      return mission.typeName === 'ManeuverRestitutionRequest'
    },

    getCurrentAPI() {
      if (this.currentMission) {
        return this.getAPI(this.currentMission.typeName + 's')
      }
    },

    getAPI(objectAPI) {
      return '/users/' + this.currentUser.id + '/' + objectAPI.toLowerCase()
    },

    exportEphemeridesFiles() {
      let files = this.currentResultsLog.results.ephemeridesFiles
      console.log(files)
      Object.entries(files).forEach((entry) => {
        let key = entry[0]
        let value = entry[1]
        const filename = this.currentMission.name + '_' + key
        dataTools.downloadFile(filename, value.join(''), 'txt', false) // no utf8
      })
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css?family=Roboto');
</style>
